// Every word People and the person page show, and the pure rules that turn the staff reads into those
// words (AdminPeople.dc.html and AdminPerson.dc.html, with Adam's answers of 4 Oct). Pure: it imports
// only the date helpers, so tests/e2e/admin-people.spec.js checks these on their own.
//
// Adam's writing rules: British English, numerals, no exclamation marks, no dashes, straight quotes,
// sentence case, contractions, and a person is named rather than given a pronoun. Words no board
// draws are marked "undrawn" for the stage 5 batch.
import { staffFirstName } from '../../lib/names.js'
import { uk, calendarDay, shortDate, dayMonth, clock } from '../../lib/format.js'
import { productDot } from '../ui/format.js'
import { historyWords as referralHistory } from '../referrals/words.js'

// ---- People (AdminPeople.dc.html) ------------------------------------------------------------------

export const PEOPLE = {
  eyebrow: 'Everyone with a login',
  title: 'People',
  bulk: 'Grant in bulk',
  bulkClose: 'Close bulk grant',
  csv: 'Export CSV',
  csvDone: 'CSV downloaded',
  add: 'Add a person',
  addCancel: 'Cancel',
  search: 'Search by name or email',
  statusLabel: 'Status',
  productLabel: 'Product',
  allProducts: 'All products',
  freeLabel: 'Used free',
  anyFree: 'Any',
  heads: { name: 'Name', products: 'Products', status: 'Status', seen: 'Last seen', joined: 'Joined' },
  // a row with no product to tag (a free account, staff, someone with nothing): said, so a phone card's
  // Products label never stands empty (undrawn; the registrations list's words)
  noProducts: 'Nothing paid',
  none: 'Nobody matches that. Clear the filters to see everyone.',
  count: (shown, total) => `Showing ${shown} of ${total}.`,
  more: 'Show more', // undrawn: the list is paged on the server
  loadingMore: 'Loading more', // undrawn
  csvName: 'people',
  // the 1-2-1 spaces, taken over from stage 4's /admin panel (undrawn)
  spacesTitle: '1-2-1 spaces',
  spacesLine: "A client with a space is under 1-2-1 Mentorship above, and the client's page links to the space.",
  // with 2 or more private client space products (the product-types plan, 13 Oct)
  spacesLineMany: "A client with a space is under its product above, and the client's page links to the space.",
  spaceProduct: 'Which product',
  spaceProductPick: 'Pick a product',
  spaceProductNeeded: 'Pick which product the space is on.',
  spaceProductOnly: name => `The space opens on ${name}.`,
}

// The status chips and what each asks staff_people for
export const STATUS_CHIPS = [
  { key: '', label: 'Any status' },
  { key: 'active', label: 'Active' },
  { key: 'attention', label: 'Needs attention' },
  { key: 'paused', label: 'Paused' },
  { key: 'ended', label: 'Ended' },
  // a person whose only thing is a free account from a replay's register page (docs/replay-register-spec.md 3.2)
  { key: 'free', label: 'Free account' },
]

// The boards' short product names, by slug; any other product goes by its own name
const SHORT = {
  'funding-engine': 'Funding Engine', accelerator: 'Accelerator', poyt: 'POYT', cashflow: 'PCFL',
  masterclass: 'Masterclass', mentorship: '1-2-1 Mentorship',
}
// The boards' order for products: The Funding Engine, the Accelerator, POYT, PCFL, the masterclass,
// then the rest in the catalogue's order
const BOARD_ORDER = ['funding-engine', 'accelerator', 'poyt', 'cashflow', 'masterclass', 'mentorship']
const orderOf = p => { const i = BOARD_ORDER.indexOf(p?.slug || p?.product_slug); return i < 0 ? 100 + (p?.sort_order || 0) : i }
export const byBoardOrder = list => [...(list || [])].sort((a, b) => orderOf(a) - orderOf(b))
export const shortName = p => (p && (SHORT[p.slug || p.product_slug] || p.name || p.product_name)) || ''

// The free chips' names (AdminPeople's "Used free"), by slug
const FREE_SHORT = {
  'next-deal-scorecard': 'The Next Deal Scorecard', '21-ways': '21 Ways guide',
  'restart-challenge': 'The Property Restart Challenge',
}
export const freeChipName = p => FREE_SHORT[p.slug] || p.name

// The video at /masterclass, which admin calls the masterclass (decision 44): by its slug, or a row of
// that type that came with no slug. Any other masterclass (the product-types plan) goes by its own name.
export const isTheMasterclass = p => p?.slug === 'masterclass' || p?.product_slug === 'masterclass'
  || (p?.type === 'masterclass' && !p?.slug && !p?.product_slug)

// A product in a sentence: "the masterclass" as the boards write it, otherwise its name
export const sentenceName = p => (isTheMasterclass(p) ? 'the masterclass' : p?.name || 'the product')

// Where a product lives until it moves in (CLAUDE.md: /poyt and /accelerator send people there)
const HOME_SITE = { poyt: 'course.adamslee.co.uk', accelerator: 'members.adamslee.co.uk' }
export const homeSite = p => HOME_SITE[p?.slug] || 'its own site'

// A day as the boards write it: 25 Jul, or 25 Jul 2025 when it isn't this year or the next 12 months
export function boardDay(t, nowMs = Date.now()) {
  if (!t) return ''
  const day = /^\d{4}-\d{2}-\d{2}$/.test(t) ? `${t}T12:00:00Z` : t
  const u = uk(day), n = uk(nowMs)
  const months = (u.y - n.y) * 12 + (u.m - n.m)
  return months > -6 && months < 12 ? dayMonth(day) : `${dayMonth(day)} ${u.y}`
}
// 08:12, as the person board writes a time
export function hhmm(t) {
  const u = uk(t)
  return `${String(u.hh).padStart(2, '0')}:${String(u.mm).padStart(2, '0')}`
}

// The status line on a People row (status_key, its product and date)
export function listStatus(row, productsById = new Map(), nowMs = Date.now()) {
  const p = productsById.get(row.status_product_id) || (row.products || []).find(x => x.product_id === row.status_product_id)
  const name = shortName(p)
  const date = row.status_date ? boardDay(row.status_date, nowMs) : ''
  switch (row.status_key) {
    case 'suspended': return name ? `Suspended, ${name}` : 'Suspended'
    case 'overdue': return name ? `Overdue, ${name}` : 'Overdue'
    case 'secured': return date ? `Balance due ${date}` : 'Place secured'
    case 'paused': return date ? `Paused to ${date}` : 'Paused'
    case 'active': return 'Active'
    case 'refunded': return 'Refunded'
    case 'revoked': return 'Revoked'
    case 'ended': return 'Ended'
    case 'free': return 'Free account' // undrawn: a replay's register page made it (spec 3.2)
    default: return 'Nothing open' // undrawn
  }
}

// The product tags on a People row: hub products, then ownership outside the hub, labelled
export function listTags(row, productsById = new Map()) {
  const tags = byBoardOrder(row.products || []).map(p => ({ key: p.product_id, product: { ...p, slug: p.slug }, label: shortName(p), outside: false }))
  for (const o of row.outside || []) {
    const p = productsById.get(o.product_id) || { id: o.product_id }
    tags.push({ key: `outside-${o.product_id}`, product: p, label: `${shortName(p) || 'A product'}, outside the hub`, outside: true }) // undrawn
  }
  return tags
}

export function lastSeen(t, nowMs = Date.now()) {
  if (!t) return 'Not yet'
  const days = uk(nowMs).day - uk(t).day
  if (days <= 0) return 'Today'
  if (days === 1) return 'Yesterday'
  if (days <= 60) return `${days} days ago`
  return boardDay(t, nowMs)
}

// ---- Add a person ---------------------------------------------------------------------------------

export const ADD = {
  title: 'Add a person by hand',
  name: 'Name',
  email: 'Email they log in with',
  reason: 'Reason, e.g. paid by bank transfer',
  give: 'Give them',
  source: 'How it was paid for', // undrawn label (answer 2)
  cohort: 'Cohort', // undrawn
  noCohort: 'No cohort', // undrawn
  button: 'Add and email how to log in', // C36, S08: how to log in, not a link; no pronoun for the person
  busy: 'Adding', // undrawn
  ready: 'Access is granted with your reason logged against it.',
  needs: 'Name, email and a reason are needed.',
  needsProduct: 'Pick a product to give first.', // undrawn
  needsSource: 'Pick how it was paid for: paid another way, or free.', // the functions' source_needed line
  openExisting: email => `Open the page for ${email}`, // undrawn, after "<email> is already in the hub."
  openNew: name => `Open ${name}'s page`, // undrawn
}

// ---- Grant in bulk --------------------------------------------------------------------------------

export const BULK = {
  title: 'Grant in bulk',
  line: 'Nothing changes until you press Grant. You can undo the whole batch after.',
  fromKind: 'Take them from', // undrawn (C15, answer 4)
  fromProduct: 'Everyone who has a product', // undrawn
  fromList: 'A pasted list', // C15
  from: '1. Everyone who has',
  paste: '1. Paste the list', // undrawn
  pasteHint: '1 line per person: the email, then the full name. Up to 500 lines.', // undrawn
  pastePlaceholder: 'sam.carter@example.com, Sam Carter',
  checkList: 'Check the list', // undrawn
  checking: 'Checking', // undrawn
  gets: '2. Gets',
  cohort: 'Cohort', // undrawn
  noCohort: 'No cohort', // undrawn
  who: "3. Check who's in it",
  nobody: 'Nobody has that product.',
  emptyList: 'Paste at least 1 email address.', // the function's empty_list line
  length: '4. For how long',
  lengths: [
    { value: 'same_as_buyer', label: 'Same as a buyer' },
    { value: 'until_date', label: 'Until a date' },
    { value: 'for_good', label: 'For good' },
  ],
  until: 'Until', // undrawn
  reason: 'Reason, logged on each person, e.g. Accelerator members get the masterclass',
  source: 'How it was paid for', // undrawn label (answer 2)
  email: "Email each of them that it's in their library",
  // undrawn (stage 6): the emails go 1 after another while you wait, so bulk-grant takes up to 100 at a
  // time with the box ticked; the over line is bulk-grant's own too_many_to_email words
  emailLimit: n => `Up to ${n} people at a time.`,
  hintEmailCap: (n, limit) => `Up to ${limit} people can be emailed at a time, and this batch has ${n}. Grant in smaller batches, or grant without the email.`,
  hintSame: 'Pick a different product to give.',
  hintNobody: 'Nobody to grant.',
  hintReason: 'Add a reason first.',
  hintSource: 'Pick how it was paid for first.', // undrawn
  hintDate: 'Pick the date it runs until.', // undrawn
  hintErrors: 'Fix the lines marked "Fix this" first, then check the list again.', // undrawn (bulk-grant's own line: "Fix the marked lines first, then grant again.")
  flag: 'Fix this.', // undrawn
  hintSkipped: n => `${n} already ${n === 1 ? 'has' : 'have'} it and ${n === 1 ? 'is' : 'are'} skipped.`,
  hintAll: 'Everyone ticked gets it.',
  button: n => `Grant to ${n} ${n === 1 ? 'person' : 'people'}`,
  busy: 'Granting', // undrawn
  undo: 'Undo this batch',
  undoBusy: 'Undoing', // undrawn
  tickLabel: name => `Grant to ${name}`, // undrawn, the tick box's name for a screen reader
}

// A tick-list row's grey words, from the check's status
export function bulkNote(row) {
  switch (row.status) {
    case 'has_it': return 'Already has it, so skipped'
    case 'duplicate': return 'Repeated, so skipped' // undrawn
    case 'new_person': return 'New to the hub. The grant adds this person.' // undrawn
    case 'invalid_email': return 'Not an email address' // undrawn
    case 'name_needed': return 'Needs a full name after the email' // undrawn
    case 'not_in_source': return "Isn't in that product any more, so skipped" // undrawn
    default:
      if (row.matched === 'previous' && row.current_email) return `Matched on an old email. Logs in with ${row.current_email} now.` // undrawn (C46)
      return row.email || ''
  }
}
export const bulkRowIsError = row => ['invalid_email', 'name_needed'].includes(row.status)
export const bulkRowGrants = row => row.status === 'grant' || row.status === 'new_person'

// The pasted list, 1 line each: email, then full name (a comma, a tab or spaces between them)
export function parseList(text) {
  const out = []
  for (const raw of String(text || '').split(/\r?\n/)) {
    const line = raw.trim()
    if (!line) continue
    const m = line.match(/^(\S+?)[\s,;\t]+(.+)$/)
    const email = (m ? m[1] : line).replace(/[,;]+$/, '').trim()
    const fullName = m ? m[2].replace(/^[,;\s]+/, '').trim() : ''
    out.push(fullName ? { email, full_name: fullName } : { email })
  }
  return out
}

// What the bar says once a batch is granted, and once it's undone
export function batchDone({ product, granted, skipped, emailEach, emailed = 0, failed = 0 }) {
  let line = `Granted ${sentenceName(product)} to ${granted} ${granted === 1 ? 'person' : 'people'}`
  if (skipped) line += `. ${skipped} already had it and ${skipped === 1 ? 'was' : 'were'} skipped`
  if (!emailEach) return `${line}.`
  if (failed) return `${line}. ${emailed} ${emailed === 1 ? 'has' : 'have'} been emailed, and ${failed} ${failed === 1 ? "email couldn't" : "emails couldn't"} be sent, so ${failed === 1 ? "it's" : "they're"} on Today.` // undrawn ending
  return `${line}. Each of them has been emailed.`
}
export const batchUndone = (product, n) => `Undone. ${n} ${n === 1 ? 'person no longer has' : 'people no longer have'} ${sentenceName(product)}.`

// ---- The person page (AdminPerson.dc.html) --------------------------------------------------------

// the first name, the full name for the database's "there" or a bare title, else the email (lib/names.js)
export const firstOf = p => (staffFirstName(p) || p?.email || 'them')

export const PERSON = {
  // a replay's register page (docs/replay-register-spec.md 5): undrawn
  freeAccount: (date, title) => (title ? `Free account since ${date}, from the register page for ${title}.` : `Free account since ${date}.`),
  registrations: 'Replay registrations',
  registrationsAside: 'From the register pages',
  registrationLine: ({ title, when, source, times, last }) => [
    `${title}: registered ${when}`,
    source ? `, from ${source}` : '',
    times > 1 ? `. ${times} times, last ${last}` : '',
    '.',
  ].join(''),
  crumb: 'People',
  grant: 'Grant a product',
  memberSince: d => `Member since ${d}.`,
  testLogin: 'Test login', // undrawn
  staffTag: role => `Staff, ${role}`, // undrawn
  access: 'Access',
  accessAside: first => `What ${first} can open, and why`,
  heads: first => ({ product: 'Product', status: 'Status', how: `How ${first} got it`, runs: 'Access runs' }),
  noAccess: first => `${first} has nothing open in the hub.`, // undrawn
  revoke: 'Revoke',
  grantAgain: 'Grant again',
  revokeConfirm: (product, first) => `Revoke ${product}? ${first} loses access straight away. Progress is kept, so granting it back picks up where ${first} left off.`,
  revokeStripe: ' Stripe keeps charging until you cancel the subscription in Stripe.', // undrawn (critic; functions builder's open point)
  revokeYes: 'Revoke access',
  // on a phone, the Change menu's last item (the critique's admin 17), still with its confirm
  revokeItem: 'Revoke access',
  // a card folded on a phone, its count in its heading: "History (24)"
  withCount: (title, n) => (n == null ? title : `${title} (${n})`),
  keep: 'Keep it',
  change: 'Change', // undrawn: the menu on each access row
  free: 'Free things',
  freeAside: 'Every account sees these',
  shown: 'Shown',
  hidden: 'Hidden',
  heldBack: first => `Held back from ${first}`,
  holdLabel: (name, first) => `Hold back ${name} for ${first}`,
  showLabel: (name, first) => `Show ${name} for ${first}`,
  holdReason: "Why it's held back, only you see this",
  holdReasonLabel: name => `Reason for holding back ${name}`,
  holdSave: 'Hold it back', // undrawn: the hold saves only once there's a reason
  holdCancel: 'Keep it shown', // undrawn
  holdNeedsReason: 'Add a reason first. Only staff see it.',
  heldReason: r => `Reason: ${r}`, // undrawn
  freeFoot: first => `Switching 1 off hides it from ${first} only. It's logged in the history below with your reason.`,
  progress: 'Progress',
  noProgress: first => `Nothing for ${first} to make progress on yet.`, // undrawn
  issue: 'Issue certificate',
  issued: 'Certificate issued',
  login: 'Logging in',
  resend: 'Resend login email', // C36, S08
  resent: 'Login email sent',
  logOut: 'Log out everywhere',
  loggedOut: 'Logged out everywhere',
  notes: 'Notes',
  notesLine: 'Only you see these.',
  notePlaceholder: first => `Add a note about ${first}`,
  saveNote: 'Save note',
  noteLabel: 'Add a note',
  history: 'History',
  older: 'Show older', // undrawn
  noHistory: 'Nothing has happened yet.', // undrawn
  cardLink: 'Send card link',
  cardSent: 'Link sent',
  extend: 'Give 7 more days',
  extended: d => `Extended to ${d}`,
  openSpace: 'Open the space', // undrawn
  endSpace: 'End the space',
  openNewSpace: first => `Open a 1-2-1 space for ${first}`, // undrawn
  notFound: "There's no one with that id.",
  notFoundTitle: 'Not found', // undrawn
  notFoundLine: 'The record may have been merged into another. Go back to People to find it.', // undrawn
  back: 'Back to People', // undrawn
  merged: 'Merged', // undrawn
  mergedLine: name => `This record was merged into ${name}'s.`, // undrawn
  mergedOpen: name => `Open ${name}'s page`, // undrawn
  deleted: 'Deleted person',
  deletedLine: d => `This person's details were wiped on ${d}. The access, progress and history stay against the record, so reports still add up.`, // undrawn
}

// ---- Grant a product (AdminPerson's dialog) ---------------------------------------------------------

export const GRANT = {
  title: first => `Grant ${first} a product`,
  product: 'Product',
  hasIt: 'Already has it',
  // decision 10's resit (undrawn): a product that releases by intake, with an intake they aren't in
  hasItResit: 'Already has it. Pick it for a resit',
  resitCohort: 'Intake for the resit',
  resitHelp: (first, held) => `A resit keeps ${first} in ${held.length ? held.join(' and ') : 'the intake they have'} and adds the intake you pick.`,
  resitReason: 'Resit',
  resitReasonHelp: 'A resit is logged with the reason Resit.',
  needIntake: 'Pick the intake for the resit',
  revoked: 'Revoked, progress kept',
  ended: 'Ended, progress kept', // undrawn
  cohort: 'Cohort', // undrawn
  noCohort: 'No cohort', // undrawn
  length: 'For how long',
  lengths: BULK.lengths,
  until: 'Until', // undrawn
  reason: 'Reason',
  reasonPlaceholder: 'e.g. Accelerator member, or paid by bank transfer',
  reasonHelp: "It's logged against the access, so a grant never looks like a missing payment later.",
  source: 'How it was paid for', // undrawn label (answer 2)
  email: first => `Email ${first} to say it's in the library`,
  pick: 'Pick a product first',
  needReason: 'Add a reason to grant',
  needSource: 'Pick how it was paid for', // undrawn
  needDate: 'Pick the date it runs until', // undrawn
  go: 'Grant access',
  busy: 'Granting', // undrawn
}

// ---- The Change menu on an access row (undrawn: BUILD-PLAN stage 5's pause, extend, change cohort,
// the balance date, and 7 more days) ----------------------------------------------------------------

export const CHANGE = {
  menu: product => `Change ${product}`,
  pause: 'Pause until a date',
  resume: 'Resume now',
  ends: 'Change the end date',
  cohort: 'Change cohort',
  // a mastermind's row says group (docs/mastermind-admin-plan.md 3.4)
  group: 'Change group',
  balance: 'Move the balance date',
  grace: 'Give 7 more days',
  card: 'Send card link',
  pauseUntil: 'Paused until',
  pauseReason: 'Reason',
  pauseReasonPlaceholder: 'e.g. Travelling for a month, back in November',
  pauseGo: 'Pause access',
  pauseLine: (first, product) => `${first} can't open ${product} while it's paused. It opens again by itself on the day you pick.`,
  resumeLine: (first, product) => `${first} can open ${product} again straight away.`,
  resumeGo: 'Resume access',
  endsLabel: 'Access runs until',
  endsLine: (first, product) => `${first} keeps ${product} to the end of the day you pick.`,
  endsGo: 'Save the end date',
  endsClear: 'Remove the end date',
  cohortLabel: 'Cohort',
  noCohort: 'No cohort',
  cohortGo: 'Move to this cohort',
  groupLabel: 'Group',
  noGroup: 'No group',
  groupGo: 'Move to this group',
  balanceLabel: 'Balance due',
  balanceLine: first => `Only ${first}'s date moves. The place stays secured until the balance is paid.`,
  balanceGo: 'Move the date',
  done: {
    pause: (product, d) => `Done. ${product} is paused until ${d}.`,
    resume: product => `Done. ${product} is open again.`,
    ends: (product, d) => `Done. ${product} runs until ${d}.`,
    endsClear: product => `Done. ${product} has no end date now.`,
    cohort: (product, name) => (name ? `Done. ${product} is on ${name} now.` : `Done. ${product} isn't in a cohort now.`),
    group: (product, name) => (name ? `Done. ${product} is in ${name} now.` : `Done. ${product} isn't in a group now.`),
    balance: (product, d) => `Done. The balance for ${product} is due ${d} now.`,
    grace: d => `Done. Access is suspended on ${d} unless it's paid.`,
  },
}

// ---- Staff role, and the This person box (undrawn) ----------------------------------------------

export const ROLE = {
  title: 'Staff role',
  none: 'Not staff',
  names: { owner: 'Owner', admin: 'Admin', support: 'Support', host: 'Host' },
  hostFor: 'Host for',
  hint: 'Staff open admin with 2-step login. A host sees only the people and registers of the products picked here.',
  save: 'Save the role',
  busy: 'Saving',
  own: 'This is your own login, so another owner changes your role.',
  // the role by the name the owner just picked, never as a noun with an article ("a owner") (undrawn)
  done: (first, role) => (role ? `Done. ${first}'s role is ${ROLE.names[role]} now.` : `Done. ${first} isn't staff any more.`),
  needProduct: 'Pick at least 1 product for a host.',
}

// An owner's reset of another staff member's 2-step login (reset-two-step; the critic's item 5, built at
// stage 5's review). The function's own line shows once it's done. (undrawn)
export const TWO_STEP = {
  title: '2-step login',
  hint: first => `If ${first} loses the phone with the authenticator app, a reset lets ${first} set up a new one. ${first} is logged out on every device.`,
  button: 'Reset 2-step login',
  // shared: the login also opens course.adamslee.co.uk, the only site that shares the hub's logins since migration 82
  confirm: (first, shared) => `Reset ${first}'s 2-step login? ${first} is logged out on every device, and sets up a new authenticator app the next time ${first} opens Admin.${shared ? ' That includes course.adamslee.co.uk, which uses the same login.' : ''}`,
  go: 'Reset it',
}

export const THIS = {
  title: 'This person',
  move: 'Move to another email',
  moveHint: first => `${first}'s access, progress and history stay as they are. ${first} logs in with the new email from then on, and the old address gets a note saying so.`,
  moveLabel: 'New login email',
  moveCheck: 'Check it',
  moveConfirm: (first, from, to) => `Move ${first}'s login from ${from} to ${to}?`,
  moveGo: 'Move it',
  oldEmails: 'Old login emails',
  oldEmail: (email, d) => `${email}, until ${d}`,
  merge: 'Merge with someone',
  mergeHint: 'For 2 records of the same person. Everything moves to the record you keep, and the other record goes.',
  mergeSearch: 'Find the other record by name or email',
  mergePick: 'Pick',
  mergeNone: 'Nobody else matches that.',
  mergeKeep: name => `Keep ${name}'s record`,
  mergeConfirm: (merged, kept) => `Merge ${merged} into ${kept}? Everything of ${merged}'s moves to ${kept}, and ${merged}'s record goes. It can't be undone.`,
  mergeGo: 'Merge them',
  mergeCancel: 'Keep both',
  staffNo: "A member of staff can't be merged or deleted. Take the staff role away first.",
  export: first => `Export ${first}'s data`,
  exportOpen: (first, d) => `${first} asked for a copy of the data held on ${d}.`,
  exportGo: 'Make the export and email it',
  // no open request: owner and admin can still make 1 (data-export {person_id}, since the integration)
  exportNone: first => `${first} hasn't asked for a copy. Making 1 puts it in ${first}'s Account to download for 7 days, and emails ${first} to say so.`, // undrawn
  delete: 'Delete',
  deleteHint: first => `Wipes ${first}'s name, email and details. Access, progress and history stay against the record, so reports still add up. It can't be undone.`,
  deleteShared: first => `${first}'s login also opens course.adamslee.co.uk, so it's kept there: remove ${first} on that site too.`,
  deleteButton: first => `Delete ${first}`,
  deleteConfirm: name => `Delete ${name}? It can't be undone.`,
  deleteGo: 'Delete',
}

export const SPACE = {
  name: '1-2-1 Mentorship',
  detail: '1-2-1',
  how: d => `Opened by hand, ${d}`,
  runs: 'Until the space is ended',
  ended: d => `Ended ${d}`,
  endConfirm: first => `End ${first}'s 1-2-1 space now? Once it's ended, ${first} can't open the space, its sessions, notes or files any more. You can still see everything in it.`,
  endYes: 'Yes, end it',
  endNo: 'Keep it open',
  openTitle: 'Open a 1-2-1 space',
}

// ---- An access row's words ---------------------------------------------------------------------

// The status pill: Active, Overdue (to 9 Oct once 7 more days are given), Place secured and the rest
export function accessStatus(row, { extendedTo } = {}) {
  switch (row.status) {
    case 'overdue': return extendedTo ? `Overdue to ${extendedTo}` : 'Overdue'
    case 'secured': return 'Place secured'
    case 'suspended': return 'Suspended'
    case 'paused': return 'Paused'
    case 'revoked': return 'Revoked'
    case 'ended': return row.end_reason === 'refund_ended' ? 'Refunded' : 'Ended'
    default: return 'Active'
  }
}
export const isCurrent = row => ['active', 'overdue', 'suspended', 'paused', 'secured'].includes(row.status)

// The grey line under a product's name, as AdminPerson.dc.html writes it: its cohort, or the day a cohort still to
// start begins ("Cohort 1, from 21 Oct"), or what kind of product it is. A membership's month ("Group 2, month 3 of
// 12") comes only from hub.mastermind_standing (mastermind/words.js standingDetail, which the access card asks first),
// the month the term counts from payments taken and paused days, never from calendar months, so the row and the
// Masterminds card can't drift apart (the critique's admin 2); without the standing, it's the group alone.
export function accessDetail(row, product, cohortsById = new Map(), nowMs = Date.now()) {
  const cohort = row.cohort_id ? cohortsById.get(row.cohort_id) : null
  if (cohort) {
    const starts = cohort.starts_on && isCurrent(row) ? calendarDay(cohort.starts_on) : null
    if (starts && starts.day > uk(nowMs).day) return `${cohort.name}, from ${boardDay(cohort.starts_on, nowMs)}`
    return cohort.name
  }
  switch (product?.type) {
    case 'mastermind': return 'No group' // undrawn: a membership with no group
    case 'on_demand': return 'On demand'
    case 'masterclass': return isTheMasterclass(product) ? 'Masterclass' : product.name
    case 'client_space': return '1-2-1'
    case 'live_cohort': return 'No cohort'
    default: return ''
  }
}

const money = (amounts, kind, id) => (id && amounts?.[kind]?.[id]?.display) || null
const perInterval = { month: 'a month', year: 'a year', week: 'a week', day: 'a day' }

// "How Sam got it": bought, a subscription, a place, a grant (by whom, with the reason), a test login
export function howGot(row, { billing, amounts, granter, nowMs = Date.now() } = {}) {
  const when = boardDay(row.starts_at, nowMs)
  const reason = row.reason ? ` Reason: ${row.reason}` : ''
  const by = granter ? ` by ${granter}` : ''
  switch (row.source) {
    case 'stripe': {
      if (billing?.stripe_subscription_id) {
        const price = amounts?.prices?.[billing.stripe_price_id]
        const each = price?.display ? `, ${price.display} ${perInterval[price.interval] || 'each time'}` : ''
        return `Subscription${each} since ${when}`
      }
      const paid = money(amounts, 'checkouts', billing?.stripe_checkout_session_id)
      if (row.status === 'secured' || billing?.place_price_id) return `Place secured${paid ? `, ${paid}` : ''}, ${when}` // undrawn
      return `Bought${paid ? `, ${paid}` : ''}, ${when}`
    }
    case 'manual': return `${row.batch_id ? 'Granted in bulk' : 'Granted'}${by}, ${when}.${reason}`.replace(/\.$/, '')
    case 'comp': return `Given free${by}, ${when}.${reason}`.replace(/\.$/, '') // undrawn
    case 'import': return `Moved in from the old site, ${when}.${reason}`.replace(/\.$/, '') // undrawn
    case 'test': return `Test access, ${when}` // undrawn
    default: return when
  }
}

// "Access runs": the terms line for an open row, the end date, the subscription, or how it ended
export function accessRuns(row, { product, billing, nowMs = Date.now() } = {}) {
  if (row.status === 'revoked') return 'Progress kept'
  if (row.status === 'ended') {
    const d = boardDay(row.ended_at || row.ends_at, nowMs)
    return d ? `Ended ${d}. Progress kept` : 'Progress kept' // undrawn
  }
  if (row.status === 'paused') {
    // the pause's reason, from Stripe or from staff (AdminPrices: "The pause reason from Stripe shows on
    // the person's page")
    const until = row.paused_until ? `Paused until ${boardDay(row.paused_until, nowMs)}.` : 'Paused.'
    const reason = String(row.pause_reason || '').trim()
    const why = reason ? ` ${/[.?]$/.test(reason) ? reason : `${reason}.`}` : row.paused_by === 'stripe' ? ' Paused in Stripe.' : ''
    return `${until}${why}` // undrawn
  }
  if (row.status === 'secured') {
    return row.balance_due_at ? `Opens when the balance is paid, due ${boardDay(row.balance_due_at, nowMs)}` : 'Opens when the balance is paid' // undrawn
  }
  if (row.ends_at) return `Until ${boardDay(row.ends_at, nowMs)}`
  if (billing?.stripe_subscription_id) return 'While the subscription is paid'
  const terms = String(product?.terms_line || '').trim().replace(/\.$/, '')
  return terms || 'For good' // a grant for good on a product with no terms line
}

// The overdue alert over the page (AdminPerson's). Admin's words keep "suspended" for the 7-day unpaid rule and
// "paused" for a pause Adam or Stripe set (the critique's admin 5)
export function overdueAlert(row, product, first, { extendedTo, nowMs = Date.now() } = {}) {
  const failed = row.payment_failed_at ? ` failed on ${boardDay(row.payment_failed_at, nowMs)}` : ' failed'
  const name = product?.name || 'The'
  const suspends = row.suspend_on ? boardDay(row.suspend_on, nowMs) : null
  if (extendedTo) return `${name} payment${failed}. You gave 7 more days, so access is suspended on ${extendedTo} unless it's paid.`
  return `${name} payment${failed}. ${first} sees a banner, and access is suspended${suspends ? ` on ${suspends}` : ''} unless it's paid.`
}

// ---- Free things ---------------------------------------------------------------------------------

// The free webinar replays they've watched (replay_progress rows with watched_at, which the database
// sets once they're through the product's watched share), as activity rows of the database's own
// watched_replay kind, so the Free things line reads them as it reads any other. Each is filed under its
// webinar's product; one whose webinar didn't come back says nothing.
export function replayActivity(replays = [], webinarsById = new Map()) {
  return replays.filter(r => r.watched_at && webinarsById.get(r.webinar_id)?.product_id).map(r => ({
    product_id: webinarsById.get(r.webinar_id).product_id, webinar_id: r.webinar_id,
    kind: 'watched_replay', source: 'hub', occurred_at: r.watched_at,
  }))
}

// The line under each free item: what they last did with it (stage3-for-adam 104: the latest
// scorecard click), from the activity rows, newest first
export function freeLine(product, activity, webinarsById = new Map(), nowMs = Date.now()) {
  const mine = activity.filter(a => a.product_id === product.id)
  const latest = kind => mine.filter(a => !kind || kind.includes(a.kind)).sort((a, b) => String(b.occurred_at).localeCompare(String(a.occurred_at)))[0]
  const on = t => shortDate(t, nowMs)
  // Free webinars: the board's "Came live to <webinar>, <date>" came from Zoom, which the hub never reads
  // (Adam, 5 Oct, stage 6), so only what the hub itself records is said: the latest replay watched
  // (replayActivity). Support can't read replays, so support always has the fallback.
  if (product.type === 'webinar') {
    const a = latest(['watched_replay'])
    if (!a) return 'Live webinar registrations are kept in Zoom' // undrawn: replay registrations are the hub's own (decision 57)
    const w = webinarsById.get(a.webinar_id)
    return `Watched the replay of ${w?.title || 'a webinar'}, ${on(a.occurred_at)}` // undrawn
  }
  if (product.slug === 'restart-challenge' || mine.some(a => a.kind === 'joined')) {
    const a = latest(['joined'])
    return a ? `Joined on ${on(a.occurred_at)}` : 'Not joined' // "Joined on" undrawn
  }
  if (product.type === 'resource_pack') {
    const a = latest(['downloaded', 'opened'])
    return a ? `Downloaded on ${on(a.occurred_at)}` : 'Not downloaded' // "Not downloaded" undrawn
  }
  const a = latest(['opened'])
  if (!a) return 'Not opened' // undrawn
  return `Opened on ${on(a.occurred_at)}${a.source === 'hub' ? ', from the hub' : ''}`
}

// ---- Progress ------------------------------------------------------------------------------------

// A live course's Start here rows (Profit On Your Terms's Deal analysis and Marketing setup), each with
// whether it's done, whether it's been started (a screen done or answered: a progress row) and its
// screens done, by product: { <product id>: [{ title, done, started, screensDone, screens }] }. From
// the switched-on rows and screens and the person's progress on them (data.js); a row is done once the
// database marks it, or once every screen in it is done.
export function startHereByProduct(steps, progress) {
  const mine = new Map((progress || []).map(p => [p.step_id, p]))
  const bySort = (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0) || String(a.id).localeCompare(String(b.id))
  const out = {}
  for (const row of (steps || []).filter(s => s.step_type === 'screens' && !s.parent_step_id).sort(bySort)) {
    const screens = steps.filter(s => s.step_type === 'screen' && s.parent_step_id === row.id)
    const screensDone = screens.filter(s => mine.get(s.id)?.done_at).length
    const done = Boolean(mine.get(row.id)?.done_at) || (screens.length > 0 && screensDone === screens.length)
    ;(out[row.product_id] ||= []).push({ title: row.title, done, started: screens.some(s => mine.has(s.id)), screensDone, screens: screens.length })
  }
  return out
}

// "Start here 1 of 2. Marketing setup not started." (AdminPerson.dc.html), the next row's state after
// the count: not started, or how many of its screens are done (undrawn)
export function startHereLine(rows) {
  const parts = [`Start here ${rows.filter(r => r.done).length} of ${rows.length}.`]
  const next = rows.find(r => !r.done)
  if (next) parts.push(next.started ? `${next.title} ${next.screensDone} of ${next.screens} screens done.` : `${next.title} not started.`) // the second undrawn
  return parts.join(' ')
}

// A member's Start here answers, read only, under the line (Adam, 7 Oct, the batch's question 2; no
// board draws them, so every word here is undrawn): the gate's 3 ticks, the second channel they
// recorded and each assignment's ticks, each named as its screen names it. Owner, admin and support
// see them; a host doesn't.
export const START_HERE_ANSWERS = {
  title: 'Start here answers',
  aside: first => `Only ${first} can change these.`,
  none: first => `${first} hasn't started Start here yet.`,
  count: (n, total) => `${n} of ${total} ticked`,
  channel: 'Second channel',
  noChannel: first => `${first} hasn't picked a second channel yet.`,
  ticked: 'Ticked',
  notTicked: 'Not ticked',
}

// The screens that hold a tick or a choice: the gate, the second channel and any with an assignment
const configOf = s => (s?.config && typeof s.config === 'object' ? s.config : {})
export const holdsAnswers = s => {
  const c = configOf(s)
  return c.type === 'gate' || c.type === 'channel_choice' || (Array.isArray(c.assignment?.items) && c.assignment.items.length > 0)
}

// The answers by product, from the switched-on rows (the steps Progress's line reads), the screens with
// their config, the person's answers on them (answers.ticks, an object of "0", "1" and "2" to true or
// false; answers.secondary, an option's id, with answers.secondary_label, set by the database) and the
// product's Start here rows (startHereByProduct), for whether they've started:
// { <product id>: { started, gate, channel, assignments } }, where gate and each assignment are
// { title, items: [{ label, ticked }], ticked } and channel is { title, label } (label null until
// they pick 1). A product with none of these screens has no entry. A screen never started has no
// answers, and a tick never made has no key: both read as not ticked.
export function startHereAnswersByProduct(steps, screens, answers, startHere = {}) {
  const mine = new Map((answers || []).map(a => [a.step_id, a.answers && typeof a.answers === 'object' ? a.answers : {}]))
  const rowOrder = new Map((steps || []).filter(s => s.step_type === 'screens' && !s.parent_step_id).map(r => [r.id, r.sort_order ?? 0]))
  const order = s => [rowOrder.get(s.parent_step_id) ?? 0, s.sort_order ?? 0]
  const bySort = (a, b) => order(a)[0] - order(b)[0] || order(a)[1] - order(b)[1] || String(a.id).localeCompare(String(b.id))
  const ticks = (lines, ans) => {
    const items = (lines || []).map((label, i) => ({ label: String(label), ticked: ans?.ticks?.[i] === true || ans?.ticks?.[String(i)] === true }))
    return { items, ticked: items.filter(t => t.ticked).length }
  }
  const out = {}
  for (const s of [...(screens || [])].filter(holdsAnswers).sort(bySort)) {
    const c = configOf(s)
    const ans = mine.get(s.id) || null
    const entry = (out[s.product_id] ||= {
      started: (startHere?.[s.product_id] || []).some(r => r.started || r.done), gate: null, channel: null, assignments: [],
    })
    if (c.type === 'gate' && !entry.gate) entry.gate = { title: s.title, ...ticks(c.items, ans) }
    else if (c.type === 'channel_choice' && !entry.channel) {
      const picked = ans?.secondary || null
      const option = (c.options || []).find(o => o.id === picked)
      entry.channel = { title: START_HERE_ANSWERS.channel, label: picked ? ans.secondary_label || option?.label || String(picked) : null }
    }
    if (Array.isArray(c.assignment?.items) && c.assignment.items.length) {
      entry.assignments.push({ title: c.assignment.label || s.title, ...ticks(c.assignment.items, ans) })
    }
  }
  return out
}

// How far through, as a whole percentage, for the gold bar. A live course before its first week is
// open (no recording released to them) goes by its Start here rows, as the board's 50% for "Start here
// 1 of 2" does; from then on by its weeks' recordings.
export function progressPct(p, startHere = null) {
  if (p.finished_at) return 100
  if (p.product_type === 'masterclass') {
    if (!p.last_duration_seconds) return 0
    return Math.min(100, Math.round((p.last_position_seconds || 0) / p.last_duration_seconds * 100))
  }
  if (p.product_type === 'mastermind') return p.sessions_held ? Math.round(p.sessions_present / p.sessions_held * 100) : 0
  if (p.product_type === 'live_cohort' && !p.recordings_released) {
    if (startHere?.length) return Math.round(startHere.filter(r => r.done).length / startHere.length * 100)
    if (p.steps_total) return Math.round(p.steps_done / p.steps_total * 100)
  }
  if (p.recordings_total) return Math.round(p.recordings_watched / p.recordings_total * 100)
  if (p.steps_total) return Math.round(p.steps_done / p.steps_total * 100)
  return 0
}

const stoppedAt = p => (p.last_position_seconds !== null && p.last_position_seconds !== undefined ? clock(p.last_position_seconds) : null)

// The line under each product's bar, as the person board writes them. startHere: the product's Start
// here rows (startHereByProduct), when they could be read.
export function progressLine(p, nowMs = Date.now(), startHere = null) {
  const parts = []
  const mods = (p.modules || []).filter(m => m.is_published)
  switch (p.product_type) {
    case 'masterclass': {
      if (p.finished_at) parts.push(`Finished on ${shortDate(p.finished_at, nowMs)}.`) // undrawn
      else if (p.last_duration_seconds && stoppedAt(p)) parts.push(`Stopped at ${stoppedAt(p)} of ${clock(p.last_duration_seconds)}.`)
      else parts.push('Not started.') // undrawn
      parts.push(p.offer_reached_at ? `The offer showed on ${shortDate(p.offer_reached_at, nowMs)}.` : 'The offer is still hidden.') // "showed on" undrawn
      break
    }
    case 'live_cohort': {
      // the weeks open to them: as many modules, in order, as their released recordings cover
      // the last module whose recordings are all open to them, counting in order
      let cum = 0, open = 0
      mods.forEach((m, i) => { cum += m.recordings || 0; if (m.recordings > 0 && cum <= (p.recordings_released || 0)) open = i + 1 })
      if (mods.length && open) parts.push(`${mods[open - 1].label} of ${mods.length}.`)
      const done = mods.slice(0, Math.max(0, open - 1)).filter(m => m.homework_total > 0)
      if (done.length) parts.push(`Homework ${done.map(m => `${m.homework_done} of ${m.homework_total} in ${m.label}`).join(', ')}.`)
      if (startHere?.length) parts.push(startHereLine(startHere))
      else if (p.steps_total) parts.push(`${p.steps_done} of ${p.steps_total} steps done.`) // undrawn
      if (p.last_module_label && stoppedAt(p)) parts.push(`Stopped ${p.last_module_label} at ${stoppedAt(p)}.`)
      if (p.finished_at) parts.push(`Finished on ${shortDate(p.finished_at, nowMs)}.`) // undrawn
      break
    }
    case 'on_demand': {
      const ticked = mods.filter(m => m.homework_total > 0 && m.homework_done >= m.homework_total)
      if (ticked.length) parts.push(`${ticked[ticked.length - 1].label} homework all ticked.`)
      const on = mods.find(m => m.recordings > 0 && m.watched < m.recordings && m.number !== null)
      if (on) parts.push(`On ${on.label}, ${on.title}.`)
      if (p.steps_total) parts.push(`${p.steps_done} of ${p.steps_total} steps done.`) // undrawn
      if (p.finished_at) parts.push(`Finished on ${shortDate(p.finished_at, nowMs)}.`) // undrawn
      break
    }
    case 'mastermind':
      parts.push(p.sessions_held ? `Attendance ${p.sessions_present || 0} of ${p.sessions_held}.` : 'No sessions held yet.') // the second undrawn
      break
    case 'client_space':
      parts.push(`${p.recordings_watched} of ${p.recordings_released} session ${p.recordings_released === 1 ? 'recording' : 'recordings'} watched.`) // undrawn
      break
    default:
      if (p.recordings_total) parts.push(`${p.recordings_watched} of ${p.recordings_released} ${p.recordings_released === 1 ? 'recording' : 'recordings'} watched.`) // undrawn
  }
  if (!parts.length) parts.push('Not started.') // undrawn
  if (p.certificate_number) parts.push(`Certificate ${p.certificate_number}.`) // undrawn
  return parts.join(' ')
}

// ---- Logging in ----------------------------------------------------------------------------------

const DEVICE = { phone: 'on a phone', laptop: 'on a laptop', tablet: 'on a tablet', desktop: 'on a computer', computer: 'on a computer' }

export function loginLine(person, nowMs = Date.now()) {
  const first = firstOf(person)
  const how = person.password_set_at ? 'by email code or password' : 'by email code' // "or password" undrawn
  const parts = [`Logs in with ${person.email} ${how}.`]
  if (person.last_login_at) {
    const days = uk(nowMs).day - uk(person.last_login_at).day
    const when = days <= 0 ? `today at ${hhmm(person.last_login_at)}` : days === 1 ? `yesterday at ${hhmm(person.last_login_at)}` : `on ${shortDate(person.last_login_at)} at ${hhmm(person.last_login_at)}`
    const device = DEVICE[person.last_login_device] ? `, ${DEVICE[person.last_login_device]}` : ''
    parts.push(`Last in ${when}${device}.`)
  } else {
    parts.push(`${first} hasn't logged in yet.`) // undrawn
  }
  // undrawn: since migration 82 (the Accelerator's old site forwards to the hub), only POYT's site shares a login
  if (person.login_shared) parts.push('The same login opens course.adamslee.co.uk.')
  return parts.join(' ')
}

// ---- History -----------------------------------------------------------------------------------

const GREY = 'var(--shell-ink-30)'
const STAFF = 'var(--shell-ink-45)'
const GOLD = 'var(--shell-gold)'

export function historyWhen(h, nowMs = Date.now()) {
  const d = boardDay(h.happened_at, nowMs)
  return h.source === 'login' ? `${d}, ${hhmm(h.happened_at)}` : d
}

const DEVICE_WORD = { phone: 'on a phone', laptop: 'on a laptop', tablet: 'on a tablet', desktop: 'on a computer' }
const EMAILS = {
  access: p => `Access email sent for ${p}`,
  resend_login: () => 'Login email sent',
  card_link: (p, first) => `Card update link sent to ${first}`,
  certificate: p => `Certificate email sent for ${p}`,
  question_reply: () => 'Reply to a question emailed',
  data_export: () => 'Data export emailed',
  login_changed: () => 'Note sent to the old login email',
}

// A replay watched, as the History row person_history would give for the database's watched_replay
// kind, so historyLine writes "Watched the replay of <webinar>" on the day it was watched
export function replayHistory(replays = [], webinarsById = new Map()) {
  return replays.filter(r => r.watched_at).map(r => {
    const w = webinarsById.get(r.webinar_id)
    return {
      happened_at: r.watched_at, source: 'activity', event: 'watched_replay', product_id: w?.product_id || null,
      product_name: null, access_id: null, actor_person_id: null, actor_name: null, actor_kind: 'member',
      detail: { webinar_id: r.webinar_id, ...(w?.title ? { webinar_title: w.title } : {}), source: 'hub' },
    }
  })
}

// History's rows: person_history's pages so far, newest first, with the replays watched put in their
// place. Until the last page is in, only the replays as new as the oldest row so far go in, so a
// replay never shows above rows that came before it; an activity row already naming the same webinar's
// replay (the database's own kind) isn't said twice.
export function historyWithReplays(rows, replayRows, done) {
  const told = new Set(rows.filter(h => h.source === 'activity' && h.event === 'watched_replay').map(h => h.detail?.webinar_id).filter(Boolean))
  const at = h => Date.parse(h.happened_at)
  const oldest = rows.length ? at(rows[rows.length - 1]) : null
  const extra = replayRows.filter(r => !told.has(r.detail?.webinar_id) && (done || (oldest !== null && at(r) >= oldest)))
    .sort((a, b) => at(b) - at(a))
  if (!extra.length) return rows
  const out = []
  let i = 0
  for (const h of rows) {
    while (i < extra.length && at(extra[i]) > at(h)) out.push(extra[i++])
    out.push(h)
  }
  while (i < extra.length) out.push(extra[i++])
  return out
}

// 1 History line, or null for an event that another line already tells (or that says nothing new).
// ctx: productsById, accessById, billingByAccess, amounts, cohortsById, granters (access id: name), first
export function historyLine(h, ctx) {
  const { productsById = new Map(), accessById = new Map(), billingByAccess = new Map(), amounts = {}, cohortsById = new Map(), sessionsById = new Map(), first = 'them', nowMs = Date.now() } = ctx || {}
  // a Stripe event names no product when its prices aren't on it (an invoice): find it by its subscription
  const viaSub = !h.product_id && h.detail?.subscription_id
    ? [...billingByAccess.values()].find(b => b.stripe_subscription_id === h.detail.subscription_id) : null
  const viaRow = viaSub ? accessById.get(viaSub.access_id) : null
  const productId = h.product_id || viaRow?.product_id || null
  const product = productsById.get(productId) || (productId ? { id: productId, name: h.product_name } : null)
  // in a sentence the masterclass is "the masterclass", as the boards write it (sentenceName)
  const isMasterclass = isTheMasterclass(product)
  const name = isMasterclass ? sentenceName(product) : h.product_name || product?.name || 'a product'
  const Name = name.charAt(0).toUpperCase() + name.slice(1) // at the start of a line
  const sourceRow = viaRow
  const dot = product ? productDot(product) : GREY
  const d = h.detail || {}
  const by = h.actor_name ? ` by ${h.actor_name}` : ''
  const line = (text, colour = dot) => ({ text, dot: colour })
  switch (h.source) {
    case 'access': {
      const billing = billingByAccess.get(h.access_id)
      if (h.event === 'started') {
        const granter = ctx?.granters?.get(h.access_id)
        const gby = granter ? ` by ${granter}` : ''
        const reason = d.reason ? `. Reason: ${d.reason}` : ''
        switch (d.source) {
          case 'stripe': {
            if (billing?.stripe_subscription_id) {
              const price = amounts?.prices?.[billing.stripe_price_id]
              return line(`Joined ${name}${price?.display ? `, ${price.display} ${perInterval[price.interval] || ''}`.trimEnd() : ''}`)
            }
            const paid = money(amounts, 'checkouts', billing?.stripe_checkout_session_id)
            if (d.status === 'secured' || billing?.place_price_id) return line(`Secured a place on ${name}${paid ? `, ${paid}` : ''}`) // undrawn
            return line(`Bought ${name}${paid ? `, ${paid}` : ''}. Access opened`)
          }
          case 'manual': return line(`Granted ${name}${d.batch_id ? ' in bulk' : ''}${gby}${reason}`)
          case 'comp': return line(`Given ${name} free${gby}${reason}`) // undrawn
          case 'import': return line(`Moved in from the old site with ${name}`) // undrawn
          case 'test': return line(`Test access to ${name}`) // undrawn
          default: return line(`Access to ${name} opened`)
        }
      }
      const revoker = ctx?.revokers?.get(h.access_id)
      if (h.event === 'revoked') {
        if (d.end_reason === 'batch_undone') return line(`Bulk grant of ${name} undone${revoker ? ` by ${revoker}` : ''}`, STAFF) // undrawn
        return line(`Revoked ${name}${revoker ? ` by ${revoker}` : ''}. Progress kept`, STAFF)
      }
      if (d.end_reason === 'refund_ended') return line(`Access to ${name} ended after a refund`, GOLD) // undrawn
      if (d.end_reason === 'expired') return line(`Access to ${name} reached its end date`) // undrawn
      if (d.end_reason === 'merged') return line(`Access to ${name} ended in a merge`, STAFF) // undrawn
      return line(`Access to ${name} ended`) // undrawn
    }
    case 'audit': {
      const after = d.after || {}
      switch (h.event) {
        case 'person_created': return h.actor_kind === 'staff' ? line(`Added to the hub${by}`, STAFF) : null // undrawn
        case 'pause': return line(`Paused ${name}${after.paused_until ? ` until ${boardDay(after.paused_until, nowMs)}` : ''}${by}`, STAFF) // undrawn
        case 'resume': return line(`${Name} opened again${by}`, STAFF) // undrawn
        case 'extend': return line(after.ends_at ? `${Name} now runs until ${boardDay(after.ends_at, nowMs)}${by}` : `End date taken off ${name}${by}`, STAFF) // undrawn
        case 'change_cohort': {
          const c = cohortsById.get(after.cohort_id)
          return line(c ? `Moved to ${c.name} on ${name}${by}` : `Taken out of a cohort on ${name}${by}`, STAFF) // undrawn
        }
        case 'move_due_date': return line(`Balance for ${name} now due ${boardDay(after.balance_due_at, nowMs)}${by}`, GOLD) // undrawn
        case 'grace_extended': return line(`Gave 7 more days on the failed payment${after.suspend_on ? `. Suspended on ${boardDay(after.suspend_on, nowMs)} if unpaid` : ''}`, GOLD)
        case 'log_out_everywhere': return line(`Logged out everywhere${by}`, STAFF) // undrawn
        case 'view_as_member_start': return line(`${h.actor_name || 'Staff'} viewed as ${first}`, STAFF) // undrawn
        case 'merge': return line(`Another record merged in${by}`, STAFF) // undrawn
        case 'move_email': return line(`Login email changed${by}`, STAFF) // undrawn
        case 'role_change': return line(`Staff role changed${by}`, STAFF) // undrawn
        case 'scope_change': return line(`Host products changed${by}`, STAFF) // undrawn
        // referrals (decision 55, migration 79): the 15 actions' words are src/admin/referrals/words.js's (undrawn)
        default: {
          const text = referralHistory(h.event, after, name)
          if (!text) return null
          return line(`${text}${h.actor_kind === 'staff' ? by : ''}`, ['introduction_qualified', 'introduction_paid'].includes(h.event) ? GOLD : STAFF)
        }
      }
    }
    case 'stripe_event': {
      const invoice = money(amounts, 'invoices', d.invoice_id)
      switch (h.event) {
        case 'invoice.payment_failed': {
          const row = sourceRow || accessById.get(h.access_id) || [...accessById.values()].find(a => a.product_id === productId && a.status === 'overdue')
          const pauses = row?.suspend_on ? `, access suspended on ${boardDay(row.suspend_on, nowMs)} if unpaid` : ''
          return line(`${Name} payment${invoice ? ` of ${invoice}` : ''} failed. Banner shown${pauses}`, GOLD)
        }
        case 'charge.refunded': return line(`Refund on ${name}${money(amounts, 'charges', d.charge_id) ? `, ${money(amounts, 'charges', d.charge_id)}` : ''}`, GOLD) // undrawn
        case 'charge.dispute.created': return line(`Payment for ${name} disputed`, GOLD) // undrawn
        case 'customer.subscription.deleted': return line(`Subscription to ${name} cancelled in Stripe`, GOLD) // undrawn
        case 'customer.subscription.paused': return line(`Subscription to ${name} paused in Stripe`, GOLD) // undrawn
        case 'customer.subscription.resumed': return line(`Subscription to ${name} resumed in Stripe`, GOLD) // undrawn
        default: return null
      }
    }
    case 'payment': {
      if (h.event !== 'paid' || d.billing_reason === 'subscription_create') return null
      const invoice = money(amounts, 'invoices', d.invoice_id)
      return line(`Paid${invoice ? ` ${invoice}` : ''} for ${name}`, GOLD) // undrawn
    }
    case 'email': {
      const word = EMAILS[h.event]
      if (!word) return null
      const text = word(name, first)
      return line(d.status === 'failed' ? `${text}, but it couldn't be sent` : text, h.event === 'card_link' ? GOLD : STAFF)
    }
    case 'hold':
      return line(`${h.event === 'held' ? 'Held back' : 'Showed again'} ${name}${by}${h.event === 'held' && d.reason ? `. Reason: ${d.reason}` : ''}`, STAFF)
    case 'certificate':
      if (h.event === 'revoked') return line(`Certificate ${d.number} for ${name} revoked`, STAFF) // undrawn
      return line(d.issued_how === 'by_hand' ? `Certificate for ${name} issued by hand${by}` : `Certificate ${d.number} for ${name} issued`, GOLD) // undrawn tail
    case 'login': {
      const device = DEVICE_WORD[d.device] ? ` ${DEVICE_WORD[d.device]}` : ''
      return line(h.event === 'password' ? `Logged in with a password${device}` : `Logged in${device}`, GREY)
    }
    case 'attendance': {
      // a mastermind's group session (no week): the group and the session's number in it, as its
      // recording is titled ("Group 2, session 2"); a week keeps its title ("Cohort 3, Week 3")
      const number = !d.module_label && d.cohort_name ? sessionsById.get(d.session_id)?.number : null
      const where = [d.cohort_name, number ? `session ${number}` : d.session_title].filter(Boolean).join(', ') || name
      if (h.event === 'present') return line(`Present at ${where}`)
      if (h.event === 'absent') return line(`Missed ${where}`) // undrawn
      return line(`Excused from ${where}`) // undrawn
    }
    case 'activity':
      switch (h.event) {
        case 'opened': return line(`Opened ${name}`, GREY) // undrawn
        case 'downloaded': return line(`Downloaded ${name}`, GREY) // undrawn
        case 'joined': return line(`Joined ${name}`, GREY) // undrawn
        case 'watched_replay': return line(`Watched the replay of ${d.webinar_title || 'a webinar'}`, GREY) // undrawn
        default: return null
      }
    case 'progress': {
      const what = [name, d.module_label].filter(Boolean).join(' ')
      switch (h.event) {
        case 'played':
          if (d.watched_at) return null
          return line(`Watched ${what} up to ${clock(d.position_seconds || 0)}`, GREY)
        case 'watched': return line(`Watched ${what}`, GREY) // undrawn
        case 'step_done': return line(`Ticked ${d.title} on ${name}`, GREY) // undrawn
        case 'offer_reached': return line(`Reached the offer in ${name}`, GREY) // undrawn
        case 'finished': return line(`Finished ${name}`, GOLD) // undrawn
        case 'client_played': return d.watched_at ? null : line(`Watched 1-2-1 session ${d.number} up to ${clock(d.position_seconds || 0)}`, GREY) // undrawn
        default: return null
      }
    }
    case 'question':
      if (h.event === 'replied') return line(`Question answered${by}`, STAFF) // undrawn
      return line(`Asked a question${product ? ` on ${name}` : ''}: "${String(d.body || '').slice(0, 140)}${String(d.body || '').length > 140 ? '...' : ''}"`) // undrawn
    case 'data_request':
      if (h.event === 'export') return line('Asked for a copy of the data held', STAFF) // undrawn
      if (h.event === 'delete') return line('Asked to be deleted', STAFF) // undrawn
      if (h.event === 'done') return line(`Data request handled${by}`, STAFF) // undrawn
      if (h.event === 'declined') return line(`Data request declined${by}`, STAFF) // undrawn
      return null
    default:
      return null
  }
}

// Who granted and who revoked each access row, from the audit rows in the history (owner and admin)
export function actorsByAccess(history) {
  const granters = new Map(), revokers = new Map()
  for (const h of history || []) {
    if (h.source !== 'audit' || !h.access_id || !h.actor_name) continue
    if (h.event === 'grant' && !granters.has(h.access_id)) granters.set(h.access_id, h.actor_name)
    if (h.event === 'revoke' && !revokers.has(h.access_id)) revokers.set(h.access_id, h.actor_name)
  }
  return { granters, revokers }
}
